import { motion, useReducedMotion } from 'motion/react'
import { HomeEmberScene } from '@/components/game3d/HomeEmberScene'
import type { CharacterShape } from '@/game/characters'
import type { HomeNeglect } from '@/game/neglect'

interface HomeWindowProps {
  accent: string
  emissive?: string
  shape?: CharacterShape
  equippedProps?: string[]
  neglect?: HomeNeglect
  characterId?: string
}

interface Spore {
  id: number
  left: number
  size: number
  delay: number
  duration: number
  drift: number
}

const SPORES: Spore[] = Array.from({ length: 14 }, (_, i) => ({
  id: i,
  left: (i * 37) % 100,
  size: 2 + ((i * 13) % 5),
  delay: (i * 0.7) % 6,
  duration: 7 + ((i * 3) % 6),
  drift: ((i % 3) - 1) * 16,
}))

export function HomeWindow({ accent, emissive, shape, equippedProps, neglect, characterId }: HomeWindowProps) {
  const reduced = useReducedMotion()
  const homeLight = neglect?.light ?? 1
  const emberEnergy = neglect?.emberEnergy ?? 1
  const shadowOpacity = 0.4 + (1 - homeLight) * 0.3

  return (
    <motion.div
      className="relative w-full max-w-sm aspect-[4/3] rounded-[var(--r-xl)] overflow-hidden border border-[var(--edge)]/60"
      style={{ boxShadow: 'var(--shadow-raised)' }}
      initial={{ opacity: 0, scale: 0.94, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 220, damping: 26 }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(120% 90% at 50% 8%, ${accent}${homeLight > 0.8 ? '22' : '14'}, transparent 55%), linear-gradient(180deg, var(--surface-sunken) 0%, var(--ink) 70%)`,
        }}
      />

      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={`hill-${i}`}
            className="absolute left-1/2 rounded-[50%]"
            style={{
              width: `${170 - i * 20}%`,
              height: `${60 - i * 8}%`,
              bottom: `${-34 + i * 9}%`,
              x: '-50%',
              background: `radial-gradient(60% 100% at 50% 0%, ${accent}${i === 0 ? '33' : i === 1 ? '22' : '18'}, transparent 72%)`,
              filter: 'blur(1px)',
            }}
            animate={reduced ? undefined : { y: [0, i % 2 === 0 ? -4 : 4, 0] }}
            transition={{ duration: 9 + i * 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}

        {!reduced &&
          SPORES.map((s) => (
            <motion.span
              key={s.id}
              className="absolute rounded-full"
              style={{
                left: `${s.left}%`,
                bottom: '-6%',
                width: s.size,
                height: s.size,
                background: accent,
                boxShadow: `0 0 8px ${accent}`,
              }}
              animate={{ y: [0, -240], x: [0, s.drift], opacity: [0, 0.9 * homeLight, 0] }}
              transition={{ duration: s.duration, delay: s.delay, repeat: Infinity, ease: 'easeOut' }}
            />
          ))}
      </div>

      <div className="absolute inset-0">
        <HomeEmberScene
          color={accent}
          emissive={emissive}
          shape={shape}
          equippedProps={equippedProps}
          reducedMotion={!!reduced}
          homeLight={homeLight}
          emberEnergy={emberEnergy}
          characterId={characterId}
        />
      </div>

      <div
        className="absolute inset-0 pointer-events-none"
        style={{ boxShadow: `inset 0 0 48px rgba(0,0,0,${shadowOpacity})` }}
      />
      <div className="absolute inset-2.5 rounded-[calc(var(--r-xl)-8px)] border border-[var(--edge)]/50 pointer-events-none" />
      <div
        className="absolute left-1/2 top-2.5 bottom-2.5 w-px -translate-x-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent, var(--edge), transparent)', opacity: 0.4 }}
      />
      <div
        className="absolute top-1/2 left-2.5 right-2.5 h-px -translate-y-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, var(--edge), transparent)', opacity: 0.4 }}
      />
    </motion.div>
  )
}
